// # Feedback Manager Service
import { db } from '../../auth/firebase';
import { collection, setDoc, getDocs, updateDoc, doc, query, orderBy, deleteDoc } from 'firebase/firestore';
import { deleteField } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

class FeedbackManager {
  constructor() {
    // # Initialize Firebase Functions
    this.functions = getFunctions(undefined, 'europe-west1');
    this.createFeedbackNotification = httpsCallable(this.functions, 'createFeedbackNotification');
  }

  // # Get current year document ID
  getYearDocId() {
    const now = new Date();
    return `${now.getFullYear()}`;
  }

  // # Add new feedback
  async addFeedback(feedbackData) {
    try {
      // # Create feedback ID
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0].replace(/-/g, '');
      const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '');
      const uniqueId = Math.random().toString(36).substr(2, 9);
      const feedbackId = `feedback_${dateStr}_${timeStr}_${uniqueId}`;

      // # Create feedback document
      const feedbackDoc = {
        feedbackId,
        metadata: {
          createdAt: now.toISOString(),
          updatedAt: now.toISOString(),
          by_email: feedbackData.email,
          by_name: feedbackData.name,
          read_status: false
        },
        content: feedbackData.content.replace(/[^\w\s.,!?-]/g, '') // Remove emojis and special chars
      };

      // # Get current year document ID
      const yearDocId = this.getYearDocId();
      const feedbacksRef = collection(db, 'feedbacks');
      const yearDoc = doc(feedbacksRef, yearDocId);

      // # Add or update year document
      await setDoc(yearDoc, {
        [feedbackId]: feedbackDoc
      }, { merge: true });

      // # Create notification for admins
      await this.createFeedbackNotification({
        feedbackData: {
          feedbackId,
          email: feedbackData.email,
          name: feedbackData.name,
          content: feedbackData.content
        }
      });

      return { success: true, feedbackId };
    } catch (error) {
      console.error('Error adding feedback:', error);
      throw error;
    }
  }

  // # Get all feedbacks (for admin)
  async getAllFeedbacks() {
    try {
      const feedbacksRef = collection(db, 'feedbacks');
      const snapshot = await getDocs(feedbacksRef);
      
      let allFeedbacks = [];
      snapshot.docs.forEach(doc => {
        // Extract feedbacks from each year document
        const yearData = doc.data();
        Object.entries(yearData).forEach(([feedbackId, feedback]) => {
          allFeedbacks.push({
            id: feedbackId,
            yearDoc: doc.id,
            ...feedback
          });
        });
      });

      // Sort by createdAt date
      return allFeedbacks.sort((a, b) => 
        new Date(b.metadata.createdAt) - new Date(a.metadata.createdAt)
      );
    } catch (error) {
      console.error('Error getting feedbacks:', error);
      throw error;
    }
  }

  // # Update feedback read status
  async updateReadStatus(feedbackId, yearDocId) {
    try {
      const feedbackRef = doc(db, 'feedbacks', yearDocId);

      await updateDoc(feedbackRef, {
        [`${feedbackId}.metadata.read_status`]: true,
        [`${feedbackId}.metadata.updatedAt`]: new Date().toISOString()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error updating feedback status:', error);
      throw error;
    }
  }

  // # Delete feedback
  async deleteFeedback(feedbackId, yearDocId) {
    try {
      const feedbackRef = doc(db, 'feedbacks', yearDocId);
      
      // Create an update object that removes the specific feedback
      const update = {
        [feedbackId]: deleteField()
      };

      await updateDoc(feedbackRef, update);
      return { success: true };
    } catch (error) {
      console.error('Error deleting feedback:', error);
      throw error;
    }
  }

  // # Clear all feedbacks from a year
  async clearAllFeedbacks(yearDocId) {
    try {
      const feedbackRef = doc(db, 'feedbacks', yearDocId);
      await deleteDoc(feedbackRef);
      return { success: true };
    } catch (error) {
      console.error('Error clearing feedbacks:', error);
      throw error;
    }
  }
}

export default new FeedbackManager();
